<template>
  <div>
    <FancyServerTable
      ref="tableRef"
      :data="logsList"
      :columns="columns"
      :loading="loading"
      :total-rows="logsListTotalCount"
      :page-index="serverOptions.page - 1"
      :page-size="serverOptions.rowsPerPage"
      :initial-sorting="[{ id: 'created_on', desc: true }]"
      :pagination-sizes="[10, 25, 50, 100]"
      :enable-column-resizing="true"
      :enable-column-visibility="true"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-dynamic-height="true"
      @fetch-data="handleFetchData"
      @update:state="handleTableStateChange"
    >
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="configLoading"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @update-config="handleUpdateConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
      <template #actions>
        <button
          class="tw-flex tw-text-xs tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-white tw-bg-secondary-500 tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
          type="button"
          @click="handleExportLogs"
          :disabled="loading"
        >
          <FileExport />
          <span>{{ t('common.actions.export') }}</span>
        </button>
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { useFancyServerTableConfig } from '@/_helpers/composables/fancy-server-table-config.composable';
import type { LogInList } from '@/_helpers/interfaces/history-logs/logs-in-list.interface';
import FileExport from '@/assets/icons/FileExport.svg';
import {
  FancyServerTable,
  FancyTableConfigManager,
  FieldType,
  type FancyTableColumnDef,
  type ServerTableOptions,
} from '@/components/shared/fancyTables';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useHistoryLogStore } from '@/stores/history-logs.store';
import { format } from 'date-fns';
import { computed, h, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

const toast = useToast();
const { t } = useI18n();
const router = useRouter();
const { setBreadcrumbs } = useBreadcrumbs();

const tableRef = ref<InstanceType<typeof FancyServerTable>>();
const logsList = ref<LogInList[]>([]);
const loading = ref(false);
const logsListTotalCount = ref(0);

// Server options for pagination and sorting
const serverOptions = ref({
  page: 1,
  rowsPerPage: 10,
  sortBy: 'created_on',
  sortType: 'desc' as 'asc' | 'desc',
});

const { getLogsList, exportLogs } = useHistoryLogStore();

// Use the configuration management composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  configLoading,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
} = useFancyServerTableConfig({
  tableName: 'history_log',
  fancyTableRef: tableRef,
  onConfigApplied: async config => {
    // When a config is applied, fetch data with the new state
    await getLogsTableList();
  },
});

// Define columns for FancyServerTable
const columns = computed<FancyTableColumnDef<LogInList>[]>(() => [
  {
    id: 'description',
    accessorKey: 'description',
    header: t('history_log.titles.action_title'),
    size: 400,
    enableSorting: true,
    enableColumnFilter: false,
    cell: (info: any) => {
      const row = info.row.original;
      const isClickable =
        row.object_ct === 'purchaseorder' ||
        row.object_ct === 'payment' ||
        (row.object_ct === 'customer' && row.action !== 'DELETE');

      return h(
        'div',
        {
          class: isClickable
            ? 'tw-cursor-pointer hover:tw-text-primary-600 hover:tw-underline'
            : '',
          onClick: isClickable ? () => performActionOnLog(row) : undefined,
        },
        info.getValue() || '--'
      );
    },
  },
  {
    id: 'action',
    accessorKey: 'action',
    header: t('history_log.titles.action_type'),
    size: 200,
    enableSorting: true,
    enableColumnFilter: false,
  },
  {
    id: 'user_name',
    accessorKey: 'user_name',
    header: t('history_log.titles.username'),
    size: 300,
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.TEXT,
  },
  {
    id: 'created_on',
    accessorKey: 'created_on',
    header: t('history_log.titles.action_date'),
    size: 220,
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterFn: 'dateRange',
    cell: info => {
      const value = info.getValue() as string;
      if (!value) return '--';
      return format(new Date(value), 'dd.MM.yyyy');
    },
  },
  {
    id: 'project_name',
    accessorKey: 'project_name',
    header: t('history_log.titles.project'),
    size: 200,
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.TEXT,
    cell: info => info.getValue() || '--',
  },
  {
    id: 'customer_name',
    accessorKey: 'customer_name',
    header: t('history_log.titles.customer_name'),
    size: 200,
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.TEXT,
  },
]);

onMounted(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.history_log',
    },
  ]);
  await getLogsTableList();
});

// Handle fetch data from FancyServerTable
const handleFetchData = async (options: ServerTableOptions) => {
  // Update server options
  serverOptions.value.page = options.pageIndex + 1;
  serverOptions.value.rowsPerPage = options.pageSize;

  // Handle sorting
  if (options.sorting.length > 0) {
    serverOptions.value.sortBy = options.sorting[0].id;
    serverOptions.value.sortType = options.sorting[0].desc ? 'desc' : 'asc';
  }

  // Build filter criteria
  const filterCriteria: any = {};
  console.log(options.filters);

  options.filters.forEach(filter => {
    console.log(filter.id);
    if (filter.id === 'created_on' && filter.value) {
      // Handle date range filter
      const dateFilter = filter.value as {
        startDate?: string;
        endDate?: string;
      };
      if (dateFilter.startDate && dateFilter.endDate) {
        filterCriteria['created_on_after'] = dateFilter.startDate;
        filterCriteria['created_on_before'] = dateFilter.endDate;
      }
    } else if (filter.value) {
      // Handle text filters
      filterCriteria[filter.id] = filter.value;
    }
  });

  await getLogsTableList(filterCriteria);
};

const getLogsTableList = async (filterCriteria: any = {}) => {
  loading.value = true;

  const sortBy = serverOptions.value.sortBy;
  const sortDirection = serverOptions.value.sortType === 'asc' ? '' : '-';

  try {
    const response = await getLogsList({
      page: serverOptions.value.page,
      page_size: serverOptions.value.rowsPerPage,
      ordering: sortDirection + sortBy,
      ...filterCriteria,
    });

    logsList.value = [...response.results];
    logsListTotalCount.value = response.count;
  } catch (error) {
    toast.open({
      message: t('common.messages.error_loading_data'),
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

const handleExportLogs = async () => {
  loading.value = true;
  const sortBy = serverOptions.value.sortBy;
  const sortDirection = serverOptions.value.sortType === 'asc' ? '' : '-';

  // Get current filters from table
  const tableState = tableRef.value?.getSerializedState();
  const filterCriteria: any = {};

  if (tableState?.columnFilters) {
    tableState.columnFilters.forEach(filter => {
      if (filter.id === 'created_on' && filter.value) {
        const dateFilter = filter.value as {
          startDate?: string;
          endDate?: string;
        };
        if (dateFilter.startDate && dateFilter.endDate) {
          filterCriteria['created_on_after'] = dateFilter.startDate;
          filterCriteria['created_on_before'] = dateFilter.endDate;
        }
      } else if (filter.value) {
        filterCriteria[filter.id] = filter.value;
      }
    });
  }

  try {
    await exportLogs({
      ordering: sortDirection + sortBy,
      ...filterCriteria,
    });
    toast.open({
      message: t('history_log.titles.we_got_your_request_please_check_your_email_for_the_export'),
      type: 'success',
    });
  } catch (error: any) {
    toast.open({
      message: error.response?.data?.errors?.[0]?.detail || t('common.messages.error'),
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

const performActionOnLog = (logData: LogInList) => {
  if (logData.object_ct === 'purchaseorder') {
    router.push({
      name: 'order',
      params: {
        id: logData.object_id,
      },
    });
  } else if (logData.object_ct === 'payment') {
    router.push({
      name: 'payment',
      params: {
        id: logData.object_id,
      },
    });
  } else if (logData.object_ct === 'customer' && logData.action !== 'DELETE') {
    router.push({
      name: 'view-customer',
      params: {
        id: logData.object_id,
      },
    });
  } else {
    toast.open({
      message: t('history_log.data.no_details'),
      type: 'warning',
    });
  }
};

// Handle table state changes using the composable
const handleTableStateChange = (state: any) => {
  debouncedHandleTableStateChange(state);
};
</script>
